import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Tooltip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CancelIcon from '@mui/icons-material/Cancel';
import { format } from 'date-fns';
import axios from '../../utils/axios';

const ParticipantsList = ({ activity, participants: propParticipants, onParticipantUpdate }) => {
  const { t } = useTranslation();
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [error, setError] = useState(null);

  // Use participants from props if provided, otherwise use activity.participants
  const participants = propParticipants || activity?.participants || [];

  const handleOpenDialog = (participant) => {
    setSelectedParticipant(participant);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setSelectedParticipant(null);
    setOpenDialog(false);
    setError(null);
  };

  const handleCancelRegistration = async () => {
    try {
      console.log('Cancelling registration:', {
        activityId: activity._id,
        userId: selectedParticipant.userId
      });
      
      const response = await axios.delete(
        `/api/activities/${activity._id}/participants/${selectedParticipant.userId}`
      );
      
      if (response.data) {
        onParticipantUpdate();
        handleCloseDialog();
        if (window.showSnackbar) {
          window.showSnackbar(t('registrationCancelledSuccess'), 'success');
        }
      }
    } catch (error) {
      console.error('Error canceling registration:', error);
      const errorMessage = error.response?.data?.message || t('registrationCancelledError');
      setError(errorMessage);
      if (window.showSnackbar) {
        window.showSnackbar(errorMessage, 'error');
      }
    }
  };

  if (!activity) {
    return (
      <Box>
        <Alert severity="error">{t('activityNotFound')}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('name')}</TableCell>
              <TableCell>{t('email')}</TableCell>
              <TableCell>{t('numberOfParticipants')}</TableCell>
              <TableCell>{t('location')}</TableCell>
              <TableCell>{t('remainingActivities')}</TableCell>
              <TableCell>{t('registeredAt')}</TableCell>
              <TableCell align="right">{t('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {participants.map((participant) => (
              <TableRow key={participant.userId}>
                <TableCell>{participant.name}</TableCell>
                <TableCell>{participant.email}</TableCell>
                <TableCell>{participant.numberOfParticipants || 1}</TableCell>
                <TableCell>{participant.location}</TableCell>
                <TableCell>
                  {participant.remainingActivities} / {participant.maxActivities}
                </TableCell>
                <TableCell>
                  {format(new Date(participant.registeredAt), 'dd/MM/yyyy HH:mm')}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={t('cancelRegistration')}>
                    <IconButton
                      color="error"
                      onClick={() => handleOpenDialog(participant)}
                    >
                      <CancelIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {participants.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {t('noParticipants')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{t('confirmCancelRegistration')}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography>
            {t('confirmCancelRegistrationText')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('cancel')}</Button>
          <Button onClick={handleCancelRegistration} color="error" variant="contained">
            {t('confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ParticipantsList; 